<template>
	<span class="upt" :class="['upt-'+kind, { 'upt-selected':(selected && !disabled), 'upt-disabled':disabled, 'shimmer':(kind === 'loading'), 'with-img':!useIcon }]" :title="label">
		<img v-if="avatar" class="avt" :class="'avatar-'+kind" :src="avatar" :alt="name">
		<sym-icon v-else-if="icon" :name="icon" class="avt-icon" :class="'avatar-'+kind"></sym-icon>
		<img v-else class="avt" :class="'avatar-'+kind" :src="avatarUrl" :alt="name">
		<span class="upt-label">{{ name }}</span>
	</span>
</template>

<script setup lang="ts">
import {ref, computed, onMounted} from "vue";
import {UserPartyKind} from "@fd/editors/user-select/UserSelectUtils.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";

const props = withDefaults(defineProps<{
	id: string|number,
	name: string,
	kind?: UserPartyKind,
	title?: string,
	avatar?: string,
	icon?: string,
	selected?: boolean,
	disabled?: boolean
}>(), {
	kind: "user",
	selected: false,
	disabled: false
});

const userAvatar = ref<string>(null);
const groupAvatar = ref<string>(null);
const partyAvatar = ref<string>(null);

/** The tag will render with a font icon and not an avatar image */
const useIcon = computed<boolean>(() => {
	return !props.avatar && !!props.icon;
});

const label = computed<string>(() => props.title ?? props.id.toString());

const avatarUrl = computed<string>(() => {
	return props.avatar ?? userAvatar.value;
});


onMounted(() => {
	if (import.meta.env.DEV) {
		userAvatar.value = new URL("../../assets/user-avatar.png", import.meta.url).href;
		groupAvatar.value = new URL("../../assets/group-avatar.png", import.meta.url).href;
		partyAvatar.value = new URL("../../assets/party-avatar.png", import.meta.url).href;
	} else if (import.meta.env.PROD) {
		userAvatar.value = new URL(/* @vite-ignore */ "../img/user-avatar.png", import.meta.url).href;
		groupAvatar.value = new URL(/* @vite-ignore */ "../img/group-avatar.png", import.meta.url).href;
		partyAvatar.value = new URL(/* @vite-ignore */ "../img/party-avatar.png", import.meta.url).href;
	}
});

</script>

<style>
.upt {
	--avatar-size: 18px;
	display: inline flex;
	flex-direction: row;
	column-gap: 2px;
	align-items: baseline;
	padding-inline: 1px;
	border-radius: calc(var(--avatar-size) / 2 + 2px);
	user-select: none;
	-webkit-user-select:none;

	background-color: var(--pill-bg);
	color: var(--pill-fg);
	box-shadow: var(--pill-shadow);

	/* This tag uses img avatar */
	&.with-img {
		position: relative;
	}

	&:hover {
		background-color: var(--pill-bg-hover);
	}

	&.upt-selected {
		background-color: var(--pill-selected-bg);
		color: var(--pill-selected-fg);
		box-shadow: var(--pill-selected-shadow);

		&:hover {
			background-color: var(--pill-selected-bg-hover);
		}
	}
	&.upt-disabled {
		background-color: var(--pill-disabled-bg);
		color: var(--pill-disabled-fg);
		box-shadow: var(--pill-disabled-shadow);

		&:hover {
			background-color: var(--pill-disabled-bg);
		}
	}

	&:has(> .avatar-group) {
		border-radius: calc(var(--avatar-size) / 4 + 2px);
	}
}
.avt-icon {
	margin-left:6px;
}
.avt {
	position:absolute;
	left: 2px;
	top: 2px;
	display: inline-block;
	width: var(--avatar-size);
	height: var(--avatar-size);
	background-color: color-mix(in srgb, var(--bg) 70%, var(--fg));
	border-radius: 50%;

	&.avatar-group {
		border-radius: 25%;
	}
}
.upt-label {
	margin-right: 6px;
}

.classic, .light {
	.upt.upt-party:not(.upt-selected):not(.upt-disabled):not(:hover) {
		background-color: #E0E8FF;
	}
	.upt.upt-system:not(.upt-selected):not(.upt-disabled):not(:hover) {
		background-color: #E0FFE8;
	}
}

.dark {
	.upt.upt-party:not(.upt-selected):not(.upt-disabled):not(:hover) {
		background-color: #004488;
	}
	.upt.upt-system:not(.upt-selected):not(.upt-disabled):not(:hover) {
		background-color: #006633;
	}
}

.with-img .upt-label {
	margin-left: calc(var(--avatar-size) + 4px);
	margin-right: 6px;
}
</style>